import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import socketService from '../services/socketService';
import APIKit from '../../config/APIKit';

const ChatContext = createContext();

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within ChatProvider');
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState({}); // { conversationId: [messages] }
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [typingUsers, setTypingUsers] = useState({}); // { conversationId: [userId] }
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Support both old and new store structure
  const loggedUser = useSelector((state) => state.auth || state.logged || {});
  const [currentUserId, setCurrentUserId] = useState(null);

  // Fetch current user ID from API
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        // Try to get from state first
        const userIdFromState = loggedUser?.user?.id || loggedUser?.user?.user?.id || loggedUser?.id || loggedUser?.userData?.id;
        
        if (userIdFromState) {
          setCurrentUserId(userIdFromState);
          return;
        }

        // Fallback: fetch from API
        const res = await APIKit.get('/me');
        if (res && res.data) {
          const userId = res.data.id || res.data.user?.id || res.id;
          setCurrentUserId(userId);
        }
      } catch (error) {
        console.error('Error fetching current user:', error);
      }
    };

    fetchCurrentUser();
  }, [loggedUser]);

  // Helper functions (defined first for use in setupSocketListeners)
  const updateConversationUnread = useCallback((conversationId, increment = true) => {
    setConversations((prev) =>
      prev.map((conv) =>
        conv.id === conversationId
          ? { ...conv, unreadCount: (conv.unreadCount || 0) + (increment ? 1 : -1) }
          : conv
      )
    );
  }, []);

  const markMessageAsRead = useCallback(async (messageId) => {
    try {
      await APIKit.put(`/chat/messages/${messageId}/read`);
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  }, []);

  const showNotification = useCallback((message) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const conversation = conversations.find((c) => c.id === message.conversationId);
      const senderName = conversation?.otherUser?.firstName || 'Someone';
      
      new Notification(`${senderName} sent a message`, {
        body: message.content,
        icon: conversation?.otherUser?.avatar || '/default-avatar.jpg',
        tag: `chat-${message.conversationId}`
      });
    }
  }, [conversations]);

  // Setup socket event listeners
  const setupSocketListeners = useCallback(() => {
    // Receive new message
    socketService.on('receive-message', (data) => {
      const { conversationId, message } = data;
      
      setMessages((prev) => ({
        ...prev,
        [conversationId]: [...(prev[conversationId] || []), message]
      }));

      // Update conversation last message
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === conversationId
            ? { ...conv, lastMessage: message, updatedAt: message.createdAt }
            : conv
        )
      );

      // Update unread count if not active conversation
      if (activeConversation?.id !== conversationId) {
        setUnreadCount((prev) => prev + 1);
        updateConversationUnread(conversationId, true);
      } else {
        // Mark as read if active
        markMessageAsRead(message.id);
      }

      // Show notification
      showNotification(message);
    });

    // User online
    socketService.on('user-online', (data) => {
      setOnlineUsers((prev) => new Set([...prev, data.userId]));
    });

    // User offline
    socketService.on('user-offline', (data) => {
      setOnlineUsers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(data.userId);
        return newSet;
      });
    });

    // Typing indicator
    socketService.on('typing', (data) => {
      const { conversationId, userId } = data;
      if (userId !== currentUserId) {
        setTypingUsers((prev) => ({
          ...prev,
          [conversationId]: [...(prev[conversationId] || []), userId]
        }));

        // Auto remove typing after 3 seconds
        setTimeout(() => {
          setTypingUsers((prev) => ({
            ...prev,
            [conversationId]: (prev[conversationId] || []).filter((id) => id !== userId)
          }));
        }, 3000);
      }
    });

    // Stop typing
    socketService.on('stop-typing', (data) => {
      const { conversationId, userId } = data;
      setTypingUsers((prev) => ({
        ...prev,
        [conversationId]: (prev[conversationId] || []).filter((id) => id !== userId)
      }));
    });
  }, [currentUserId, activeConversation]);

  // Load conversations
  const loadConversations = useCallback(async () => {
    try {
      setLoading(true);
      const res = await APIKit.get('/chat/conversations');
      if (res.status) {
        setConversations(res.data || []);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load messages for a conversation
  const loadMessages = useCallback(async (conversationId) => {
    try {
      const res = await APIKit.get(`/chat/messages/${conversationId}`);
      if (res.status) {
        setMessages((prev) => ({
          ...prev,
          [conversationId]: res.data || []
        }));
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  }, []);

  // Send message
  const sendMessage = useCallback(async (conversationId, content, recipientId) => {
    try {
      const messageData = {
        conversationId,
        content,
        recipientId,
        senderId: currentUserId
      };

      // Send via socket for real-time
      socketService.emit('send-message', messageData);

      // Also save via API for persistence
      const res = await APIKit.post('/chat/messages', messageData);
      if (res.status && res.data) {
        const newMessage = res.data;
        
        setMessages((prev) => ({
          ...prev,
          [conversationId]: [...(prev[conversationId] || []), newMessage]
        }));

        // Update conversation
        setConversations((prev) =>
          prev.map((conv) =>
            conv.id === conversationId
              ? { ...conv, lastMessage: newMessage, updatedAt: newMessage.createdAt }
              : conv
          )
        );

        return newMessage;
      }
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }, [currentUserId]);

  // Create or get conversation
  const getOrCreateConversation = useCallback(async (otherUserId) => {
    try {
      const res = await APIKit.post('/chat/conversations', {
        userId: otherUserId
      });
      
      if (res.status && res.data) {
        const conversation = res.data;
        
        // Add to conversations if not exists
        setConversations((prev) => {
          const exists = prev.find((c) => c.id === conversation.id);
          if (!exists) {
            return [...prev, conversation];
          }
          return prev;
        });

        return conversation;
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }
  }, []);

  // Mark conversation as read
  const markConversationAsRead = useCallback(async (conversationId) => {
    try {
      await APIKit.put(`/chat/conversations/${conversationId}/read`);
      
      // Update local state
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === conversationId ? { ...conv, unreadCount: 0 } : conv
        )
      );

      // Update unread count
      const conversation = conversations.find((c) => c.id === conversationId);
      if (conversation?.unreadCount) {
        setUnreadCount((prev) => Math.max(0, prev - conversation.unreadCount));
      }
    } catch (error) {
      console.error('Error marking conversation as read:', error);
    }
  }, [conversations]);

  // Typing indicator
  const sendTyping = useCallback((conversationId, isTyping) => {
    if (!conversationId) return;
    
    socketService.emit(isTyping ? 'typing' : 'stop-typing', {
      conversationId,
      userId: currentUserId
    });
  }, [currentUserId]);

  // Request notification permission
  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  }, []);

  // Load unread count
  const loadUnreadCount = useCallback(async () => {
    try {
      const res = await APIKit.get('/chat/unread-count');
      if (res.status) {
        setUnreadCount(res.data?.count || 0);
      }
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  }, []);

  // Initialize on mount
  useEffect(() => {
    if (currentUserId) {
      loadConversations();
      loadUnreadCount();
      requestNotificationPermission();
    }
  }, [currentUserId, loadConversations, loadUnreadCount, requestNotificationPermission]);

  // Mark conversation as read when active
  useEffect(() => {
    if (activeConversation) {
      markConversationAsRead(activeConversation.id);
      loadMessages(activeConversation.id);
    }
  }, [activeConversation, markConversationAsRead, loadMessages]);

  const value = {
    conversations,
    activeConversation,
    setActiveConversation,
    messages: messages[activeConversation?.id] || [],
    onlineUsers,
    typingUsers: typingUsers[activeConversation?.id] || [],
    unreadCount,
    loading,
    sendMessage,
    getOrCreateConversation,
    loadConversations,
    loadMessages,
    markMessageAsRead,
    markConversationAsRead,
    sendTyping,
    requestNotificationPermission,
    currentUserId
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

